import { useMemo, useState } from "react";
import { RiderGHIRandGAMERRandBB, SelectedRider } from "../../../../features/gms-proposal-forms/attachable-rider/attachable-riders.state";
import { AmountOfLoansRider } from "../../../../features/gms-proposal-forms/amount-of-loans/amount-loans.state";
import { UniformCoverageRiderItem } from "../../../../features/gms-proposal-forms/coverage/uniform-coverage/coverage-type-uniform-coverage.state";
import { LevelRankingRider } from "../../../../features/gms-proposal-forms/coverage/level-ranking/coverage-type-level-ranking.state";
import { SalaryRankRider } from "../../../../features/gms-proposal-forms/coverage/salary-rank/coverage-type-by-salary-rank.state";
import { PROPOSAL_RIDERS } from "../../../../constant/gms-constant";
import { formatAmountType } from "../../../../utils/format-numbers.utils";

interface RiderConfigurationProps {
    selectedRiders?: SelectedRider[];
    amountOfLoansRiders?: AmountOfLoansRider[];
    uniformCoverageRiders?: UniformCoverageRiderItem[];
    levelRankingRiders?: LevelRankingRider[];
    salaryRankRiders?: SalaryRankRider[];
    selectedBMSR?: RiderGHIRandGAMERRandBB | null;
    isAmountOfLoan?: boolean;
    isUniformCoverage?: boolean;
    isLevelRanking?: boolean;
    isSalaryRank?: boolean;
}

interface ProposedAmendRidersProps {
    isShow: boolean;
    current: RiderConfigurationProps;
    proposed: RiderConfigurationProps;
}

type RiderValueSource = "amountOfLoans" | "uniformCoverage" | "levelRanking" | "salaryRank" | "BMSR" | "selectedRider";

interface ComparableRiderValue {
    source: RiderValueSource;
    designation: string;
    amount: number;
}

interface ComparableRider {
    riderId: number;
    label: string;
    values: ComparableRiderValue[];
}

type RiderChangeType = "added" | "removed" | "changed";

interface RiderComparison {
    riderId: number;
    current?: ComparableRider;
    proposed?: ComparableRider;
    type: RiderChangeType;
}

const normalizeString = (value: unknown): string => {
    if (value === null || value === undefined) return "";
    return String(value).trim();
};

const normalizeAmount = (value: unknown): number => {
    if (value === null || value === undefined || value === "") return 0;
    const parsed = Number(String(value).replace(/,/g, "").trim());
    return Number.isNaN(parsed) ? 0 : parsed;
};

const normalizeComparableValues = (values: ComparableRiderValue[]): ComparableRiderValue[] => {
    return [...values].sort((a, b) => {
        const sourceCompare = a.source.localeCompare(b.source);
        if (sourceCompare !== 0) return sourceCompare;

        const designationCompare = a.designation.localeCompare(b.designation);
        if (designationCompare !== 0) return designationCompare;

        return a.amount - b.amount;
    });
};

const areRiderValuesEqual = (current?: ComparableRider, proposed?: ComparableRider): boolean => {
    if (!current || !proposed) return false;
    if (current.riderId !== proposed.riderId) return false;

    const currentValues = normalizeComparableValues(current.values);
    const proposedValues = normalizeComparableValues(proposed.values);

    if (currentValues.length !== proposedValues.length) return false;

    return currentValues.every((currentValue, index) => {
        const proposedValue = proposedValues[index];

        return currentValue.source === proposedValue.source && currentValue.designation === proposedValue.designation && currentValue.amount === proposedValue.amount;
    });
};

const getRiderOrder = (): number[] => {
    return [
        PROPOSAL_RIDERS.GTPDRC.productFor.GCLI,
        PROPOSAL_RIDERS.GADBR.productFor.GYRT,
        PROPOSAL_RIDERS.GADDR.productFor.GYRT,
        PROPOSAL_RIDERS.GADDR_PLUS.productFor.GYRT,
        PROPOSAL_RIDERS.GSADDR.productFor.GYRT,
        PROPOSAL_RIDERS.GTPDR.productFor.GYRT,
        PROPOSAL_RIDERS.GTIR.productFor.GYRT,
        PROPOSAL_RIDERS.GHIR.productFor.GYRT,
        PROPOSAL_RIDERS.GAMERR.productFor.GYRT,
        PROPOSAL_RIDERS.ALCR.productFor.GYRT,
        PROPOSAL_RIDERS.BMSR.productFor.GYRT,
        PROPOSAL_RIDERS.GMR.productFor.GYRT,
        PROPOSAL_RIDERS.GDR.productFor.GYRT,
        PROPOSAL_RIDERS.GMBDR.productFor.GYRT,
        PROPOSAL_RIDERS.GCIR5.productFor.GYRT,
        PROPOSAL_RIDERS.GCIR45.productFor.GYRT,
        PROPOSAL_RIDERS.GAMERR.productFor.GPA,
        PROPOSAL_RIDERS.GMR.productFor.GPA,
        PROPOSAL_RIDERS.GMBDR.productFor.GPA,
        PROPOSAL_RIDERS.GCIR5.productFor.GPA,
        PROPOSAL_RIDERS.GCIR45.productFor.GPA,
        PROPOSAL_RIDERS.GADBR.productFor.GCI,
        PROPOSAL_RIDERS.GADDR.productFor.GCI,
        PROPOSAL_RIDERS.GADDR_PLUS.productFor.GCI,
        PROPOSAL_RIDERS.GTPDR.productFor.GCI,
        PROPOSAL_RIDERS.GTIR.productFor.GCI,
        PROPOSAL_RIDERS.GHIR.productFor.GCI,
        PROPOSAL_RIDERS.GAMERR.productFor.GCI,
        PROPOSAL_RIDERS.ALCR.productFor.GCI,
        PROPOSAL_RIDERS.BMSR.productFor.GCI,
        PROPOSAL_RIDERS.GMR.productFor.GCI,
        PROPOSAL_RIDERS.GDR.productFor.GCI,
        PROPOSAL_RIDERS.GMBDR.productFor.GCI,
    ];
};

const addValue = (riderMap: Map<number, ComparableRider>, riderId: number, riderLabel: string, value: ComparableRiderValue) => {
    const existing = riderMap.get(riderId);

    if (existing) {
        existing.values.push(value);
        return;
    }

    riderMap.set(riderId, {
        riderId,
        label: riderLabel,
        values: [value],
    });
};

const buildComparableRiders = (configuration: RiderConfigurationProps): ComparableRider[] => {
    const riderMap = new Map<number, ComparableRider>();
    const selectedRiders = configuration.selectedRiders ?? [];

    selectedRiders.forEach((rider) => {
        if (rider.id === null || rider.id === undefined) return;

        const riderId = Number(rider.id);

        if (!riderMap.has(riderId)) {
            riderMap.set(riderId, {
                riderId,
                label: rider.label ?? `Rider ${riderId}`,
                values: [],
            });
        }

        rider.inputs?.forEach((input) => {
            addValue(riderMap, riderId, rider.label, {
                source: "selectedRider",
                designation: normalizeString(input.headerName),
                amount: normalizeAmount(input.fAmount),
            });
        });
    });

    if (configuration.isAmountOfLoan) {
        (configuration.amountOfLoansRiders ?? []).forEach((item) => {
            addValue(riderMap, Number(item.id), item.label, {
                source: "amountOfLoans",
                designation: normalizeString(item.rider?.label) || "Rider Coverage",
                amount: normalizeAmount(item.rider?.fAmount),
            });
        });
    }

    if (configuration.isUniformCoverage) {
        (configuration.uniformCoverageRiders ?? []).forEach((item) => {
            addValue(riderMap, Number(item.id), item.label, {
                source: "uniformCoverage",
                designation: normalizeString(item.rider?.label) || "Rider Coverage",
                amount: normalizeAmount(item.rider?.fAmount),
            });
        });
    }

    if (configuration.isLevelRanking) {
        (configuration.levelRankingRiders ?? []).forEach((item) => {
            item.inputs?.forEach((input) => {
                addValue(riderMap, Number(item.id), item.label, {
                    source: "levelRanking",
                    designation: normalizeString(input.label),
                    amount: normalizeAmount(input.fAmount),
                });
            });
        });
    }

    if (configuration.isSalaryRank) {
        (configuration.salaryRankRiders ?? []).forEach((item) => {
            item.rider?.forEach((input) => {
                addValue(riderMap, Number(item.id), item.label, {
                    source: "salaryRank",
                    designation: normalizeString(input.designation),
                    amount: normalizeAmount(input.result),
                });
            });
        });
    }

    if (configuration.selectedBMSR) {
        const bmsr = configuration.selectedBMSR;
        const bmsrId = Number(bmsr.id ?? 0);
        const bmsrLabel = normalizeString(bmsr.label);
        const bmsrAmount = normalizeAmount(bmsr.inputs?.[0]?.fAmount);

        const isBMSRSelected = selectedRiders.some(
            (rider) => Number(rider.id) === bmsrId,
        );

        if (bmsrId > 0 && bmsrLabel && isBMSRSelected) {
            addValue(riderMap, bmsrId, bmsrLabel, {
                source: "BMSR",
                designation: "Rider Coverage",
                amount: bmsrAmount,
            });
        }
    }

    return Array.from(riderMap.values());
};

const getRiderChanges = (current: ComparableRider[], proposed: ComparableRider[]): RiderComparison[] => {
    const currentMap = new Map<number, ComparableRider>();
    const proposedMap = new Map<number, ComparableRider>();

    current.forEach((rider) => {
        currentMap.set(rider.riderId, rider);
    });

    proposed.forEach((rider) => {
        proposedMap.set(rider.riderId, rider);
    });

    const riderIds: number[] = [];

    currentMap.forEach((_, riderId) => {
        if (riderIds.indexOf(riderId) === -1) {
            riderIds.push(riderId);
        }
    });

    proposedMap.forEach((_, riderId) => {
        if (riderIds.indexOf(riderId) === -1) {
            riderIds.push(riderId);
        }
    });

    const changes: RiderComparison[] = [];

    riderIds.forEach((riderId) => {
        const currentRider = currentMap.get(riderId);
        const proposedRider = proposedMap.get(riderId);

        if (!currentRider && proposedRider) {
            changes.push({
                riderId,
                proposed: proposedRider,
                type: "added",
            });
            return;
        }

        if (currentRider && !proposedRider) {
            changes.push({
                riderId,
                current: currentRider,
                type: "removed",
            });
            return;
        }

        if (currentRider && proposedRider && !areRiderValuesEqual(currentRider, proposedRider)) {
            changes.push({
                riderId,
                current: currentRider,
                proposed: proposedRider,
                type: "changed",
            });
        }
    });

    const riderOrder = getRiderOrder();

    return changes.sort((a, b) => {
        const aIndex = riderOrder.indexOf(a.riderId);
        const bIndex = riderOrder.indexOf(b.riderId);

        if (aIndex !== -1 && bIndex !== -1) {
            return aIndex - bIndex;
        }

        if (aIndex !== -1) {
            return -1;
        }

        if (bIndex !== -1) {
            return 1;
        }

        return a.riderId - b.riderId;
    });
};

const NoChanges = () => {
    return (
        <div className="flex min-h-[72px] items-center gap-2 rounded-md bg-gray-50 px-3 py-3 text-[11px] text-gray-400">
            <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4 shrink-0" aria-hidden="true">
                <path d="m5 12.5 4.5 4L19 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>No changes</span>
        </div>
    );
};

const getSourceLabel = (source: RiderValueSource): string => {
    switch (source) {
        case "amountOfLoans":
            return "Amount of Loans";
        case "uniformCoverage":
            return "Uniform Coverage";
        case "levelRanking":
            return "Level Ranking";
        case "salaryRank":
            return "Salary Rank";
        case "BMSR":
            return "BMSR";
        default:
            return "Rider Coverage";
    }
};

const RiderValues = ({ rider, proposed }: { rider?: ComparableRider; proposed?: boolean }) => {
    if (!rider) {
        return (
            <div className="flex min-h-[70px] items-center rounded-md bg-gray-50 px-3 py-3 text-[11px] text-gray-400">
                Rider not attached
            </div>
        );
    }

    const values = normalizeComparableValues(rider.values);

    if (values.length === 0) {
        return (
            <div className="flex min-h-[70px] items-center rounded-md bg-gray-50 px-3 py-3 text-[11px] text-gray-500">
                Rider attached
            </div>
        );
    }

    return (
        <div className="overflow-hidden rounded-md border border-gray-200">
            {values.map((value, index) => (
                <div key={`${value.source}-${value.designation}-${index}`} className="grid grid-cols-1 gap-2 border-b border-gray-100 px-3 py-2.5 last:border-b-0 sm:grid-cols-[minmax(0,1fr)_140px]">
                    <div className="min-w-0">
                        <div className="mb-0.5 text-[9px] font-semibold uppercase tracking-wide text-gray-400">
                            {getSourceLabel(value.source)}
                        </div>
                        <div className="break-words text-[11px] text-gray-700">
                            {value.designation || "Rider Coverage"}
                        </div>
                    </div>
                    <div className="min-w-0 sm:text-right">
                        <div className={`mb-0.5 text-[9px] font-semibold uppercase tracking-wide ${proposed ? "text-[#186cb2]" : "text-gray-400"}`}>
                            Amount
                        </div>
                        <div className={`break-words text-[11px] font-medium ${proposed ? "text-[#186cb2]" : "text-gray-700"}`}>
                            {formatAmountType(value.amount)}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};

const RiderStatusBadge = ({ type }: { type: RiderChangeType }) => {
    const label = type === "added" ? "Added" : type === "removed" ? "Removed" : "Changed";

    const className = type === "removed"
        ? "bg-gray-100 text-gray-500 ring-gray-200"
        : "bg-[#e3f1fa] text-[#186cb2] ring-[#c7e0f3]";

    return (
        <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wide ring-1 ${className}`}>
            {label}
        </span>
    );
};

const getSideStatus = (type: RiderChangeType, side: "current" | "proposed"): RiderChangeType | null => {
    if (type === "changed") {
        return "changed";
    }

    if (type === "added" || type === "removed") {
        return side === "proposed" ? type : null;
    }

    return null;
};

const RiderCard = ({ item, side, index }: { item: RiderComparison; side: "current" | "proposed"; index: number }) => {
    const [showContent, setShowContent] = useState(false);

    const rider = side === "current" ? item.current : item.proposed;
    const riderName = rider?.label ?? item.current?.label ?? item.proposed?.label ?? "Rider";
    const sideStatus = getSideStatus(item.type, side);

    return (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
            <button
                type="button"
                onClick={() => setShowContent((previous) => !previous)}
                className="flex w-full items-center justify-between gap-3 border-b border-gray-200 px-3 py-2.5 text-left transition-colors hover:bg-gray-50"
            >
                <div className="flex min-w-0 items-center gap-2">
                    <svg viewBox="0 0 24 24" fill="none" className={`h-3.5 w-3.5 shrink-0 text-[#186cb2] transition-transform ${showContent ? "rotate-0" : "-rotate-90"}`} aria-hidden="true" >
                        <path d="m7 10 5 5 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span className="shrink-0 rounded-md bg-slate-100 px-1.5 py-0.5 text-[8px] font-semibold text-slate-500">
                        {index + 1}
                    </span>
                    <span title={riderName} className="truncate min-w-0 break-words text-[11px] font-semibold text-gray-800">
                        {riderName}
                    </span>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                    {sideStatus && (
                        <RiderStatusBadge type={sideStatus} />
                    )}
                    <span className="text-[9px] font-medium text-gray-400">
                        {showContent ? "Hide" : "Show"}
                    </span>
                </div>
            </button>
            {showContent && (
                <div className="p-3">
                    <RiderValues rider={rider} proposed={side === "proposed"} />
                </div>
            )}
        </div>
    );
};

export default function ProposedAmendRiders({ isShow, current, proposed }: ProposedAmendRidersProps) {
    const [showContent, setShowContent] = useState(true);

    const currentComparable = useMemo(
        () => buildComparableRiders(current),
        [current],
    );

    const proposedComparable = useMemo(
        () => buildComparableRiders(proposed),
        [proposed],
    );

    const changes = useMemo(
        () => getRiderChanges(currentComparable, proposedComparable),
        [currentComparable, proposedComparable],
    );

    if (!isShow || changes.length === 0) {
        return null;
    }

    return (
        <div className="grid grid-cols-1 gap-3.5">
            <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                {/* MAIN HEADER */}
                <button
                    type="button"
                    onClick={() => setShowContent((previous) => !previous)}
                    className="flex w-full items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 py-2.5 text-left transition-colors hover:bg-slate-50"
                >
                    <div className="flex min-w-0 items-center gap-2">
                        <svg viewBox="0 0 24 24" fill="none" className={`h-3.5 w-3.5 shrink-0 text-[#186cb2] transition-transform ${showContent ? "rotate-0" : "-rotate-90"}`} aria-hidden="true" >
                            <path d="m7 10 5 5 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <h2 className="text-[11px] font-semibold uppercase tracking-wide text-slate-700">
                            Attachable Riders
                        </h2>
                        <span className="rounded-full bg-[#e3f1fa] px-1.5 py-0.5 text-[8px] font-bold text-[#186cb2] ring-1 ring-[#c7e0f3]">
                            {changes.length}
                        </span>
                    </div>
                    <span className="shrink-0 text-[9px] font-medium text-slate-400">
                        {showContent ? "Hide" : "Show"}
                    </span>
                </button>

                {showContent && (
                    <>
                        {/* CURRENT / PROPOSED HEADER */}
                        <div className="grid grid-cols-1 border-b border-slate-200 sm:grid-cols-2">
                            <div className="border-b border-slate-200 bg-slate-50 px-4 py-2.5 sm:border-b-0 sm:border-r">
                                <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                    Current
                                </h3>
                            </div>
                            <div className="bg-[#f5f9fc] px-4 py-2.5">
                                <h3 className="text-[11px] font-semibold uppercase tracking-wide text-[#186cb2]">
                                    Proposed
                                </h3>
                            </div>
                        </div>
                        {/* CONTENT */}
                        <div className="grid grid-cols-1 sm:grid-cols-2">
                            {/* CURRENT */}
                            <div className="min-w-0 border-b border-slate-200 p-3 sm:border-b-0 sm:border-r sm:p-4">
                                {changes.length === 0 ? (
                                    <NoChanges />
                                ) : (
                                    <div className="space-y-3">
                                        {changes.map((item, index) => (
                                            <RiderCard
                                                key={`current-${item.riderId}`}
                                                item={item}
                                                side="current"
                                                index={index}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* PROPOSED */}
                            <div className="min-w-0 p-3 sm:p-4">
                                {changes.length === 0 ? (
                                    <NoChanges />
                                ) : (
                                    <div className="space-y-3">
                                        {changes.map((item, index) => (
                                            <RiderCard
                                                key={`proposed-${item.riderId}`}
                                                item={item}
                                                side="proposed"
                                                index={index}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}